import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, FileText, ListChecks, ClipboardList, ArrowRight, Eye, Send } from 'lucide-react';
import { adminApi, REPORT_STATUS_LABEL, REPORT_STATUS_TONE } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { useDataLabel } from '@/lib/dataI18n';
import { toast } from 'sonner';
import SuiteDocSection from '@/components/admin/SuiteDocSection';
import SuiteOpsPanel from '@/components/admin/SuiteOpsPanel';

/**
 * Generic console for a single suite. Shows the published questionnaire(s) of
 * the suite, the active assignments using it, and the recent reports.
 */
export default function SuiteConsole({
  suiteCode,
  suiteName,
  suiteTagline,
  accent = 'primary',
  testid,
  testidPrefix,
  withOps = false,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tData = useDataLabel();
  // Stable prefix for data-testids on questionnaire rows. Defaults to the
  // suite_code, but can be overridden (e.g. talentintelligence vs the
  // shared suite_code 'intelligence').
  const tp = testidPrefix || suiteCode;
  // Support i18n-key indirection so the wrapper can declare its tagline
  // without invoking the hook itself (e.g. `i18n:suite.taglines.talentheart`).
  const resolvedTagline = suiteTagline && suiteTagline.startsWith('i18n:')
    ? t(suiteTagline.slice('i18n:'.length))
    : suiteTagline;
  const [loading, setLoading] = useState(true);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [reports, setReports] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [qs, as, rs] = await Promise.all([
          adminApi.listQuestionnaires(),
          adminApi.listAssignments(),
          adminApi.listReports(),
        ]);
        if (!alive) return;
        setQuestionnaires((qs || []).filter((q) => (q.suite_codes || []).includes(suiteCode)));
        setAssignments((as || []).filter((a) => a.suite_code === suiteCode));
        setReports((rs || []).filter((r) => {
          const matched = (as || []).find((a) => a.id === r.assignment_id);
          return matched && matched.suite_code === suiteCode;
        }));
      } catch (e) {
        toast.error(t('pages.suiteConsole.loadFailed'), { description: formatApiError(e) });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [suiteCode]);

  if (loading) {
    return (
      <>
        <Topbar title={suiteName} subtitle={t('pages.suiteConsole.loading')} />
        <div className="p-12 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('pages.suiteConsole.loadingSuite', { name: suiteName })}
        </div>
      </>
    );
  }

  const total = assignments.length;
  const submitted = assignments.filter((a) => a.status === 'submitted').length;
  const inProgress = assignments.filter((a) => a.status === 'in_progress').length;
  const pending = total - submitted - inProgress;

  return (
    <>
      <Topbar title={suiteName} subtitle={resolvedTagline} />
      <div className="p-6 lg:p-8 space-y-6" data-testid={testid || `suite-${suiteCode}`}>
        {/* Founder UAT — role-aware operational supervision layer */}
        {withOps && <SuiteOpsPanel suiteCode={suiteCode} />}

        {/* KPI cards — UAT D4: clickable, drill into the filtered records */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard
            icon={ListChecks}
            label={t('pages.suiteConsole.activeQnrs')}
            value={questionnaires.filter((q) => q.status === 'published').length}
            tone={accent}
            onClick={() => navigate(`/admin/evaluation-library?suite=${suiteCode}`)}
            testid={`kpi-${suiteCode}-questionnaires`}
          />
          <KpiCard icon={ClipboardList} label={t('pages.suiteConsole.assignmentsTotal')} value={total} tone="info"
                   onClick={() => navigate(`/admin/evaluations?suite=${suiteCode}`)} testid={`kpi-${suiteCode}-assignments`} />
          <KpiCard icon={ClipboardList} label={t('pages.suiteConsole.submitted')} value={submitted} tone="success"
                   onClick={() => navigate(`/admin/evaluations?suite=${suiteCode}&status=submitted`)} testid={`kpi-${suiteCode}-submitted`} />
          <KpiCard icon={FileText} label={t('pages.suiteConsole.reports')} value={reports.length} tone="accent"
                   onClick={() => navigate('/admin/reports')} testid={`kpi-${suiteCode}-reports`} />
        </div>

        {/* Questionnaires */}
        <Card className="p-0 shadow-card overflow-hidden">
          <div className="px-6 py-5 border-b border-border flex items-center justify-between">
            <div>
              <h3 className="font-display font-bold text-lg tracking-tight">{t('pages.suiteConsole.suiteQnrs')}</h3>
              <p className="text-sm text-muted-foreground">{t('pages.suiteConsole.qnrsSeeded', { name: suiteName })}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/questionnaires')}
              data-testid={`${testid || `suite-${suiteCode}`}-manage-qs`}
              className="gap-1.5"
            >
              {t('pages.suiteConsole.manage')} <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
          {questionnaires.length === 0 ? (
            <EmptyBlock text={t('pages.suiteConsole.noQnrs', { name: suiteName })} />
          ) : (
            <div className="divide-y divide-border">
              {questionnaires.map((q) => (
                <div
                  key={q.id}
                  className="px-6 py-4 flex items-start justify-between gap-4 flex-wrap"
                  data-testid={`suite-${tp}-qnr-${q.code}`}
                >
                  <div className="min-w-0 flex-1">
                    <button
                      type="button"
                      onClick={() => navigate(`/admin/questionnaires/${q.id}`)}
                      data-testid={`suite-${tp}-qnr-name-${q.code}`}
                      className="text-left font-medium hover:text-primary hover:underline transition-colors"
                    >
                      {tData(q.name)}
                    </button>
                    <p className="text-xs text-muted-foreground mt-0.5">{tData(q.description)}</p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <code className="text-xs text-muted-foreground bg-muted/60 px-1.5 py-0.5 rounded">{q.code}</code>
                      {(q.tags || []).slice(0, 3).map((tag) => (
                        <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className={
                      q.status === 'published'
                        ? 'bg-success/10 text-success border-success/20'
                        : q.status === 'approved'
                          ? 'bg-info/10 text-info border-info/20'
                          : 'bg-muted text-muted-foreground border-border'
                    } data-testid={`suite-${tp}-qnr-status-${q.code}`}>
                      {t(`common.${q.status}`, { defaultValue: q.status })}
                    </Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`suite-${tp}-qnr-view-${q.code}`}
                      onClick={() => navigate(`/admin/questionnaires/${q.id}`)}
                      className="gap-1.5"
                    >
                      <Eye className="h-3.5 w-3.5" /> {t('pages.suiteConsole.viewQnr')}
                    </Button>
                    {q.status === 'published' && q.current_version_id ? (
                      <Button
                        size="sm"
                        data-testid={`suite-${tp}-qnr-assign-${q.code}`}
                        onClick={() => navigate(`/admin/questionnaires/${q.id}?assign=1`)}
                        className="bg-gradient-primary gap-1.5"
                      >
                        <Send className="h-3.5 w-3.5" /> {t('pages.suiteConsole.assignQnr')}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled
                        title={t('pages.suiteConsole.assignUnavailable', { defaultValue: 'Only published questionnaires can be assigned' })}
                        data-testid={`suite-${tp}-qnr-assign-disabled-${q.code}`}
                        className="gap-1.5 opacity-60"
                      >
                        <Send className="h-3.5 w-3.5" /> {t('pages.suiteConsole.assignQnr')}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Suite documentation */}
        <SuiteDocSection suiteCode={suiteCode} />

        {/* Assignments + Reports */}
        <div className="grid lg:grid-cols-2 gap-6">
          <Card className="p-0 shadow-card overflow-hidden">
            <div className="px-6 py-5 border-b border-border">
              <h3 className="font-display font-bold text-lg tracking-tight">{t('pages.suiteConsole.assignmentActivity')}</h3>
              <p className="text-sm text-muted-foreground">
                {t('pages.suiteConsole.summary', { submitted, inProgress, pending })}
              </p>
            </div>
            {assignments.length === 0 ? (
              <EmptyBlock text={t('pages.suiteConsole.noAssignments')} />
            ) : (
              <div className="divide-y divide-border max-h-[320px] overflow-y-auto">
                {assignments.slice(0, 10).map((a) => (
                  <div key={a.id} className="px-6 py-3 flex items-center justify-between text-sm">
                    <div className="min-w-0">
                      <p className="truncate">{a.title}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {t('pages.suiteConsole.candidateLabel')} · {a.assigned_to?.slice(0, 8)}…
                        {a.configuration ? ` · ${a.configuration.label}${a.configuration_resolved_via === 'default_fallback' ? ` (${t('pages.suiteConsole.cfgFallback', { defaultValue: 'predeterminada' })})` : ''}` : ''}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-xs capitalize">{(a.status || 'requested').replace(/_/g, ' ')}</Badge>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card className="p-0 shadow-card overflow-hidden">
            <div className="px-6 py-5 border-b border-border flex items-center justify-between">
              <div>
                <h3 className="font-display font-bold text-lg tracking-tight">{t('pages.suiteConsole.recentReports')}</h3>
                <p className="text-sm text-muted-foreground">{t('pages.suiteConsole.workflow')}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin/reports')}
                data-testid={`${testid || `suite-${suiteCode}`}-open-reports`}
                className="gap-1.5"
              >
                {t('pages.suiteConsole.allReports')} <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
            {reports.length === 0 ? (
              <EmptyBlock text={t('pages.suiteConsole.noReportsYet')} />
            ) : (
              <div className="divide-y divide-border max-h-[320px] overflow-y-auto">
                {reports.slice(0, 8).map((r) => {
                  const tone = REPORT_STATUS_TONE[r.status] || REPORT_STATUS_TONE.draft_ai_generated;
                  return (
                    <button
                      key={r.id}
                      onClick={() => navigate(`/admin/reports/${r.id}`)}
                      data-testid={`${testid || `suite-${suiteCode}`}-report-${r.id}`}
                      className="w-full px-6 py-3 flex items-center justify-between text-sm hover:bg-muted/30 transition-colors text-left"
                    >
                      <div className="min-w-0">
                        <p className="font-medium truncate">{r.candidate?.name || '—'}</p>
                        <p className="text-xs text-muted-foreground">{t('pages.suiteConsole.scoreLabel')} {r.scores?.overall_score ?? '—'} · {r.scores?.band || '—'}</p>
                      </div>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${tone.bg} ${tone.text} ${tone.border} border`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />
                        {REPORT_STATUS_LABEL[r.status] || r.status}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}

const TONE_STYLES = {
  primary: 'bg-primary/10 text-primary',
  accent: 'bg-accent/10 text-accent',
  success: 'bg-success/10 text-success',
  info: 'bg-info/10 text-info',
  warning: 'bg-warning/10 text-warning',
};

function KpiCard({ icon: Icon, label, value, tone = 'primary', onClick, testid }) {
  return (
    <Card
      className={`p-5 shadow-card ${onClick ? 'cursor-pointer transition-colors hover:border-primary/40' : ''}`}
      onClick={onClick}
      data-testid={testid}
      role={onClick ? 'button' : undefined}
    >
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${TONE_STYLES[tone] || TONE_STYLES.primary}`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="text-sm font-medium text-muted-foreground mt-4">{label}</p>
      <p className="text-3xl font-display font-bold mt-0.5 tabular-nums">{value}</p>
    </Card>
  );
}

function EmptyBlock({ text }) {
  return <div className="px-6 py-10 text-center text-sm text-muted-foreground">{text}</div>;
}
